<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import {
  IonTabs, IonTabBar, IonTabButton, IonIcon, IonLabel, IonRouterOutlet, IonPage,
} from '@ionic/vue';
import { flame, flameOutline, albums, heart, chatbubbles, person } from 'ionicons/icons';
import { useI18n } from '@/i18n';
import OnboardingModal from '@/components/onboarding/OnboardingModal.vue';
import NearbyAlertBanner from '@/components/match/NearbyAlertBanner.vue';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { useUserStore } from '@/stores/useUserStore';
import { pushNotificationService } from '@/services/pushNotificationService';
import { backgroundGeolocationService } from '@/services/backgroundGeolocationService';

const { t } = useI18n();
const notificationStore = useNotificationStore();
const userStore = useUserStore();

const showOnboarding = ref(false);

function startNearbyBackgroundGeo(): void {
  void backgroundGeolocationService.start({
    bgTitle: t('bg_geo_title'),
    bgMessage: t('bg_geo_notice'),
  }).catch((e) => console.warn('[bg-geo start]', e));
}

function syncBackgroundGeoToPlan(): void {
  if (userStore.isPro && userStore.user?.id) {
    startNearbyBackgroundGeo();
  } else {
    void backgroundGeolocationService.stop().catch((e) => console.warn('[bg-geo stop]', e));
  }
}

onMounted(() => {
  if (!localStorage.getItem('swipewish.onboarding_done')) {
    showOnboarding.value = true;
  }
  if (userStore.user?.id) {
    notificationStore.loadUnread().catch(() => {});
    notificationStore.subscribeRealtime();
    void pushNotificationService.init().catch((e) => console.warn('[push init]', e));
    syncBackgroundGeoToPlan();
  }
});

watch(() => userStore.user?.id, (id) => {
  if (!id) {
    notificationStore.unsubscribeRealtime();
    void backgroundGeolocationService.stop().catch(() => {});
    return;
  }
  notificationStore.loadUnread().catch(() => {});
  notificationStore.subscribeRealtime();
  void pushNotificationService.init().catch((e) => console.warn('[push init]', e));
  syncBackgroundGeoToPlan();
});

watch(() => userStore.isPro, () => {
  if (!userStore.user?.id) return;
  syncBackgroundGeoToPlan();
});

onUnmounted(() => {
  notificationStore.unsubscribeRealtime();
  void backgroundGeolocationService.stop().catch(() => {});
});
</script>

<template>
  <ion-page>
    <OnboardingModal v-if="showOnboarding" @done="showOnboarding = false" />
    <NearbyAlertBanner />
    <ion-tabs>
      <ion-router-outlet />
      <ion-tab-bar slot="bottom" class="md3-tab-bar">

      <ion-tab-button tab="inventory" href="/tabs/inventory" class="md3-tab-btn">
        <div class="tab-indicator" aria-hidden="true" />
        <ion-icon :icon="albums" class="tab-icon" />
        <ion-label class="tab-label">{{ t('tab_inventory') }}</ion-label>
      </ion-tab-button>

      <ion-tab-button tab="wishlist" href="/tabs/wishlist" class="md3-tab-btn">
        <div class="tab-indicator" aria-hidden="true" />
        <ion-icon :icon="heart" class="tab-icon" />
        <ion-label class="tab-label">{{ t('tab_wishlist') }}</ion-label>
      </ion-tab-button>

      <ion-tab-button tab="swipe" href="/tabs/swipe" class="md3-tab-btn swipe-tab-btn">
        <div class="tab-indicator" aria-hidden="true" />
        <div class="tab-icon-wrap flame-wrap" aria-hidden="true">
          <ion-icon :icon="flame" class="tab-icon red-flame flame-main" />
          <ion-icon :icon="flameOutline" class="tab-icon flame-detail" />
        </div>
        <ion-label class="tab-label">{{ t('tab_discover') }}</ion-label>
      </ion-tab-button>

      <ion-tab-button tab="matches" href="/tabs/matches" class="md3-tab-btn">
        <div class="tab-indicator" aria-hidden="true" />
        <div class="tab-icon-wrap">
          <ion-icon :icon="chatbubbles" class="tab-icon" />
          <span v-if="notificationStore.unreadCount > 0" class="match-badge" />
        </div>
        <ion-label class="tab-label">{{ t('tab_matches') }}</ion-label>
      </ion-tab-button>

      <ion-tab-button tab="profile" href="/tabs/profile" class="md3-tab-btn">
        <div class="tab-indicator" aria-hidden="true" />
        <ion-icon :icon="person" class="tab-icon" />
        <ion-label class="tab-label">{{ t('tab_profile') }}</ion-label>
      </ion-tab-button>
    </ion-tab-bar>
    </ion-tabs>
  </ion-page>
</template>

<style scoped>
/* ── MD3 Navigation Bar — floating pill ─────────────────────────────────── */
.md3-tab-bar {
  margin-left: 20px;
  margin-right: 20px;
  --background: transparent;
  height: calc(64px + env(safe-area-inset-bottom, 0px) + 14px);
  padding-top: 7px;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: 10px;
  padding-right: 10px;
  overflow: visible;
  position: relative;
}

.md3-tab-bar::before {
  content: '';
  position: absolute;
  inset: 7px 0 calc(env(safe-area-inset-bottom, 0px) + 7px);
  background: rgba(19, 26, 39, 0.88);
  -webkit-backdrop-filter: blur(22px) saturate(1.35);
  backdrop-filter: blur(22px) saturate(1.35);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  z-index: 0;
  pointer-events: none;
}

/* ── Tab button base ────────────────────────────────────────────────────── */
.md3-tab-btn {
  --color: var(--text-faint);
  --color-selected: var(--md-sys-color-primary);
  --ripple-color: transparent;
  position: relative;
  z-index: 1;
  flex-direction: column;
  gap: 0;
  padding-top: 6px;
  transition: color var(--md-duration-short4) var(--md-easing-standard);
}

/* ── Active indicator pill (MD3 spec) ──────────────────────────────────── */
.tab-indicator {
  position: absolute;
  top: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 45px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(95, 111, 241, 0.4), rgba(83, 184, 255, 0.3));
  opacity: 0;
  transition:
    width var(--md-duration-medium2) var(--md-easing-emphasized-decelerate),
    opacity var(--md-duration-short4) var(--md-easing-standard);
  pointer-events: none;
  z-index: 0;
}

/* When this tab is selected, expand the pill */
.md3-tab-btn.tab-selected .tab-indicator {
  width: 65px;
  height: 55px;
  margin-top: 3px;
  opacity: 1;
}

.tab-icon {
  font-size: 22px;
  position: relative;
  z-index: 1;
  transition:
    transform var(--md-duration-medium2) var(--md-easing-emphasized-decelerate),
    color var(--md-duration-short4) var(--md-easing-standard);
}


.red-flame {
  color: #96a5ff;
}

.flame-wrap {
  width: 28px;
  height: 28px;
  position: relative;
  isolation: isolate;
  transform-origin: 50% 100%;
  animation: flame-flicker 2.1s ease-in-out infinite;
}

.flame-wrap::before {
  content: '';
  position: absolute;
  inset: 2px;
  border-radius: 48% 52% 58% 42% / 64% 64% 36% 36%;
  background:
    radial-gradient(4% 6% at 50% 84%, rgba(170, 184, 255, 0.95) 0%, rgba(150, 165, 255, 0.9) 38%, rgba(112, 133, 246, 0.56) 72%, transparent 100%),
    radial-gradient(85% 40% at 50% 100%, rgba(142, 159, 255, 0.6) 0%, transparent 74%);
  filter: blur(0.95px);
  transform: translateY(1px);
  opacity: 0.9;
  z-index: 0;
  animation: flame-core-pulse 1.4s ease-in-out infinite;
}

.flame-wrap::after {
  content: '';
  position: absolute;
  inset: -6px -7px -5px;
  border-radius: 50%;
 
  filter: blur(2.8px);
  opacity: 0.8;
  z-index: 0;
  animation: flame-aura-drift 2.7s ease-in-out infinite;
}

.flame-main {
  z-index: 2;
  color: #96a5ff;
  filter:
    drop-shadow(0 0 1px rgba(164, 180, 255, 0.68))
    drop-shadow(0 2px 6px rgba(114, 134, 241, 0.62))
    drop-shadow(0 5px 11px rgba(100, 124, 245, 0.38));
  animation: flame-main-breathe 1.8s ease-in-out infinite;
}

.flame-detail {
  position: absolute;
  inset: 0;
  margin: auto;
  font-size: 18px;
  color: rgba(167, 182, 255, 0.92);
  transform: translateY(0.5px) scale(0.82);
  z-index: 3;
  opacity: 0.8;
  mix-blend-mode: screen;
  pointer-events: none;
  animation: flame-detail-shimmer 1.6s ease-in-out infinite;
}

.swipe-tab-btn.tab-selected .flame-wrap::before {
  opacity: 1;
  filter: blur(0.8px);
}

.swipe-tab-btn.tab-selected .flame-wrap::after {
  opacity: 1;
  filter: blur(3.4px);
}

.swipe-tab-btn.tab-selected .flame-main {
  color: #adbbff;
}

.swipe-tab-btn.tab-selected .flame-detail {
  color: rgba(181, 196, 255, 0.95);
}

@keyframes flame-flicker {
  0%, 100% { transform: translateY(0) scale(1) rotate(-1deg); }
  25% { transform: translateY(-0.4px) scale(1.015) rotate(0.8deg); }
  50% { transform: translateY(0.2px) scale(0.995) rotate(-0.6deg); }
  75% { transform: translateY(-0.3px) scale(1.01) rotate(0.5deg); }
}

@keyframes flame-core-pulse {
  0%, 100% { opacity: 0.84; transform: translateY(1px) scale(0.98); }
  45% { opacity: 1; transform: translateY(0.4px) scale(1.03); }
  70% { opacity: 0.9; transform: translateY(0.8px) scale(1); }
}

@keyframes flame-aura-drift {
  0%, 100% { opacity: 0.72; transform: scale(0.96); }
  50% { opacity: 0.92; transform: scale(1.05); }
}

@keyframes flame-main-breathe {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-0.5px) scale(1.03); }
}

@keyframes flame-detail-shimmer {
  0%, 100% { opacity: 0.72; }
  50% { opacity: 0.95; }
}

.tab-label {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.01em;
  margin-top: 2px;
  transition: font-weight var(--md-duration-short4) var(--md-easing-standard);
  position: relative;
  z-index: 1;
}
.md3-tab-btn.tab-selected .tab-label {
  font-weight: 700;
}

/* ── Match badge dot ─────────────────────────────────────────────────────── */
.tab-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.match-badge {
  position: absolute;
  top: -3px;
  right: -5px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #53b8ff;
  border: 1.5px solid rgba(19, 26, 39, 0.95);
  animation: badge-pop 0.3s var(--md-easing-emphasized-decelerate, cubic-bezier(0.05, 0.7, 0.1, 1)) both;
}
@keyframes badge-pop {
  from { transform: scale(0); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
</style>
